import { Button as BaseButton } from "@base-ui/react/button";
import classNames from "classnames";
import * as React from "react";

import variants from "./buttonVariants.module.css";
import styles from "./RobotoButton.module.css";

/**
 * Visual style of the button. `primary` is the solid brand call-to-action;
 * `secondary` is the neutral outlined control that sits beside it;
 * `destructive` is the solid red confirm for an action that cannot be undone,
 * and `destructive-secondary` the outlined red button that opens that confirm;
 * `ghost` is the borderless one for row and card-header affordances that
 * should not draw the eye until reached.
 */
export type RobotoButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "destructive-secondary"
  | "ghost";

/** Control density: drives the button's height, padding, and font size. */
export type RobotoButtonSize = "sm" | "md" | "lg";

interface RobotoButtonBaseProps extends React.AriaAttributes {
  /** Visual style. @default "primary" */
  variant?: RobotoButtonVariant;
  /** Control density. @default "md" */
  size?: RobotoButtonSize;
  /** Shows a spinner after the label and disables interaction. */
  loading?: boolean;
  /** Extra class(es) for call-site layout/positioning. */
  className?: string;
  disabled?: boolean;
  /**
   * Keep the button focusable (and its tooltip working) while disabled, for a
   * button that can become disabled under the user's focus, such as a "next"
   * that reaches the last item.
   */
  focusableWhenDisabled?: boolean;
  /** @default "button" */
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  id?: string;
  title?: string;
  /** -1 leaves the button out of the Tab order, for a widget that moves
   *  focus among its items with the arrow keys and keeps one of them
   *  tabbable. */
  tabIndex?: number;
}

/** A button with a visible label; the label is what names it. */
interface LabelledButtonProps extends RobotoButtonBaseProps {
  children: React.ReactNode;
  /**
   * Icon rendered before the children. Accepts any React element (e.g. a
   * lucide or MUI icon). The variant sizes and colors it to match the label.
   */
  icon?: React.ReactNode;
}

/**
 * A button with no label. It has nothing to derive an accessible name from, so
 * `icon` and `aria-label` are both required and omitting either is a type
 * error rather than an audit finding.
 */
interface IconOnlyButtonProps extends RobotoButtonBaseProps {
  children?: undefined;
  icon: React.ReactNode;
  "aria-label": string;
}

export type RobotoButtonProps = LabelledButtonProps | IconOnlyButtonProps;

const variantClass: Record<RobotoButtonVariant, string> = {
  primary: variants.primary,
  secondary: variants.secondary,
  destructive: variants.destructive,
  "destructive-secondary": variants.destructiveSecondary,
  ghost: variants.ghost,
};

const sizeClass: Record<RobotoButtonSize, string> = {
  sm: variants.sizeSm,
  md: variants.sizeMd,
  lg: variants.sizeLg,
};

/**
 * A standard Button component.
 *
 * @todo Rename to Button in a follow up PR and deprecate old Button
 * component as part of moving off of MUI.
 *
 * @example
 * ```tsx
 * <RobotoButton variant="primary" icon={<PlusIcon />} onClick={handleClick}>
 *   New
 * </RobotoButton>
 * ```
 *
 * @example An icon-only button. Without children, `aria-label` is required.
 * ```tsx
 * <RobotoButton variant="ghost" size="sm" icon={<ChevronUp />} aria-label="Move output earlier" />
 * ```
 */
export const RobotoButton = React.forwardRef<
  HTMLButtonElement,
  RobotoButtonProps
>(function RobotoButton(
  {
    variant = "primary",
    size = "md",
    icon,
    loading = false,
    disabled,
    type = "button",
    className,
    children,
    ...props
  },
  ref,
) {
  const { "aria-label": ariaLabel, ...rest } = props;
  const ariaLabelledBy = rest["aria-labelledby"];

  // Derive the accessible name from a string label so it survives the visible
  // label being hidden — e.g. when a toolbar collapses its buttons to
  // icon-only on small screens (see `.label` in buttonVariants.module.css).
  // Skip derivation when the caller already named the button (aria-label /
  // aria-labelledby), and only for plain-string children (JSX labels can't be
  // read out here — those callers pass their own aria-label).
  const labelText =
    ariaLabel === undefined &&
    ariaLabelledBy === undefined &&
    typeof children === "string"
      ? children
      : undefined;

  const hasLabel = children !== undefined && children !== null;

  return (
    <BaseButton
      ref={ref}
      className={classNames(
        variantClass[variant],
        sizeClass[size],
        { [variants.iconOnly]: !hasLabel },
        className,
      )}
      disabled={disabled || loading}
      type={type}
      data-loading={loading || undefined}
      aria-label={ariaLabel ?? labelText}
      {...rest}
    >
      {!loading && icon}
      {hasLabel && <span className={variants.label}>{children}</span>}
      {loading && <span className={styles.spinner} aria-hidden="true" />}
    </BaseButton>
  );
});
